import { Badge, Center, Code, Drawer, Group, Loader, Paper, Stack, Text } from '@mantine/core'
import { IconAlertCircle, IconInbox, IconPlugConnectedX, IconSearch } from '@tabler/icons-react'
import { EmptyState, MetaList } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { adminPageMayFetch } from '../../utils/adminGate'
import { useDeadLetterPeek } from '../../hooks/useQueues'
import { isApiError } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../../utils/format'
import type { DeadLetterMessage, DeadLetterQueueStats } from '../../types/queues'

/** What RestrictedState names when the caller lacks Lighthouse.Admin. */
const SURFACE = 'The dead-letter peek'

/**
 * What is sitting in one dead-letter queue: up to ten messages, read without
 * consuming them.
 *
 * Opened from the Queues page with a stats row; `null` closes it. A drawer
 * rather than a page because the reader is working down the list of queues and
 * the peek is about one row of it.
 *
 * Admin-only on the server (`DeadLetterController.PeekMessages` carries the
 * LighthouseAdmin policy) because a dead-lettered message IS its payload — a
 * report export event carries a report name and the requesting user's id. So:
 * on gate `denied` the body renders RestrictedState and never asks; on `unknown`
 * it asks, and the endpoint's own 403 renders the same restricted state.
 *
 * Read-only. There is no replay, requeue or purge control here, and none should
 * be added without its own authorization decision: replay is a manual runbook
 * step (docs/operations/dead-letter-runbook.md).
 */
export function DeadLetterPeekDrawer({
  queue,
  onClose,
}: {
  queue: DeadLetterQueueStats | null
  onClose: () => void
}) {
  return (
    <Drawer
      opened={queue !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={<Text fw={600}>What is sitting in this dead-letter queue</Text>}
    >
      {/* Keyed on the consumer, so moving to another queue remounts the body rather than flashing the last one. */}
      {queue && <DeadLetterPeekBody key={queue.sourceConsumer} queue={queue} />}
    </Drawer>
  )
}

/**
 * The drawer's contents for one queue. Exported for the render tests: Mantine's
 * Drawer portals its children, and a portal renders nothing under react-dom/server.
 */
export function DeadLetterPeekBody({ queue }: { queue: DeadLetterQueueStats }) {
  const gate = useLighthouseAdmin()
  // `null` disables the query: on `denied` this never asks the server, and on `pending`
  // (the catalogue has not answered) it waits rather than race the answer.
  const { data, isLoading, isError, error } = useDeadLetterPeek(
    adminPageMayFetch(gate) ? queue.sourceConsumer : null,
  )

  return (
    <Stack gap="md">
      <MetaList
        items={[
          { label: 'Dead-letter queue', value: queue.queueName, mono: true },
          { label: 'Consumer', value: queue.sourceConsumer, mono: true },
          { label: 'Depth reading', value: depthReading(queue) },
        ]}
      />

      {gate.state === 'denied' ? (
        <RestrictedState surface={SURFACE} reason={gate.reason} />
      ) : isError ? (
        <PeekError error={error} />
      ) : isLoading || !data ? (
        // `|| !data`: between states the flags can read "loaded, no error" with no
        // data, which must not render as "nothing came back".
        <Center py="xl"><Loader /></Center>
      ) : !Array.isArray(data) ? (
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="The peek did not answer with a list of messages"
          message="This says nothing about whether the queue holds messages."
        />
      ) : data.length === 0 ? (
        <NothingCameBack queue={queue} />
      ) : (
        <Messages messages={data} queue={queue} />
      )}
    </Stack>
  )
}

/** The stats row's depth as the page read it — unavailable is not zero. */
function depthReading(queue: DeadLetterQueueStats): string {
  if (queue.unavailable) return `${NOT_MEASURED} unavailable`
  return `${exactNumber(queue.messageCount)} waiting`
}

/** Restricted, unknown-queue and broken look different, and none of them reads as "the queue is empty". */
function PeekError({ error }: { error: unknown }) {
  const kind = errorKind(error)

  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? error.detail : null} />
  }

  if (kind === 'bad-request') {
    // The controller returns the monitor's ArgumentException message as a JSON
    // string, which fetchApi keeps as the body rather than the message. Only a
    // caller who passed authorization gets this far, so it is the admin's to read.
    const said = isApiError(error) && typeof error.body === 'string' ? firstLine(error.body) : null
    return (
      <EmptyState
        icon={<IconSearch size={28} stroke={1.5} />}
        title="The server does not have this queue configured"
        message={
          <>
            The peek accepts only a consumer queue the monitor is configured with. This says nothing about
            whether the queue holds messages.
            {said && <> The server says: {said}</>}
          </>
        }
      />
    )
  }

  if (kind === 'network') {
    return (
      <EmptyState
        icon={<IconPlugConnectedX size={28} stroke={1.5} />}
        title="Could not reach the API"
        message="The peek never got an answer. This says nothing about whether the queue holds messages."
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not peek this queue"
      message={
        `${errorSummary(error) ?? 'The request failed.'} This says nothing about whether the queue holds messages.`
      }
    />
  )
}

/**
 * An empty peek, read against the depth.
 *
 * Not proof of an empty queue: on RabbitMQ a failed read ends the peek early and
 * the endpoint still answers 200 with what it had, and the queue can also have
 * been drained since the depth was read. So the depth reading decides the
 * sentence, and a measured depth above zero is said out loud.
 */
function NothingCameBack({ queue }: { queue: DeadLetterQueueStats }) {
  const message = queue.unavailable
    ? 'The depth could not be measured either, so this does not show that the queue is empty.'
    : queue.messageCount > 0
      ? `The depth reading says ${exactNumber(queue.messageCount)} waiting, so an empty peek is not proof of an `
        + 'empty queue: on RabbitMQ a failed read ends the peek early and still answers successfully, and the '
        + 'queue may have been drained since the depth was read.'
      : 'The depth reading was also 0.'

  return (
    <EmptyState
      icon={<IconInbox size={28} stroke={1.5} />}
      title="No messages came back"
      message={message}
    />
  )
}

function Messages({ messages, queue }: { messages: DeadLetterMessage[]; queue: DeadLetterQueueStats }) {
  const count = messages.length === 1 ? '1 message' : `${exactNumber(messages.length)} messages`

  return (
    <Stack gap="sm">
      <Text size="sm">
        {count} read, up to ten per peek
        {!queue.unavailable && <>, of {exactNumber(queue.messageCount)} the depth reading says are waiting</>}.
        Nothing was removed.
      </Text>

      {messages.map((message, index) => (
        <MessageCard
          key={index}
          message={message}
          index={index}
          repeatOf={firstIndexWithId(messages, message.messageId, index)}
        />
      ))}

      <Text size="xs" c="dimmed">
        A sample, not an ordered listing. On RabbitMQ (Development) there is no native peek: the server takes
        each message and puts it straight back, so the same message can come back more than once and order is
        not kept. Service Bus (Azure) peeks in place. The delivery count is whatever the broker reports: on
        Service Bus, delivery attempts; on RabbitMQ, how many times the message has been dead-lettered (its
        x-death count). The error description is Service Bus only.
      </Text>
    </Stack>
  )
}

/**
 * The index of the first earlier message with the same id, or null.
 *
 * Marked rather than folded: on RabbitMQ a repeat is the peek reading the head
 * message twice, but on Service Bus two distinct dead-lettered messages can share
 * a publisher-set id, and folding would hide one of them. A null id matches nothing.
 */
function firstIndexWithId(messages: DeadLetterMessage[], id: string | null, before: number): number | null {
  if (id == null) return null
  const first = messages.findIndex((m) => m.messageId === id)
  return first >= 0 && first < before ? first : null
}

function MessageCard({
  message,
  index,
  repeatOf,
}: {
  message: DeadLetterMessage
  index: number
  repeatOf: number | null
}) {
  return (
    <Paper withBorder p="sm" radius="sm">
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap">
          <Text size="sm" fw={600}>Message {index + 1}</Text>
          {repeatOf !== null && (
            <Badge size="sm" variant="light" color="gray">Same id as message {repeatOf + 1}</Badge>
          )}
        </Group>

        <MetaList
          items={[
            { label: 'Subject', value: message.subject ?? NOT_MEASURED, mono: message.subject != null },
            { label: 'Message id', value: message.messageId ?? NOT_MEASURED, mono: message.messageId != null },
            { label: 'Enqueued', value: whenLabel(message.enqueuedAt) },
            {
              label: 'Delivery count',
              value: message.deliveryCount == null ? NOT_MEASURED : exactNumber(message.deliveryCount),
            },
            { label: 'Dead-letter reason', value: message.deadLetterReason ?? NOT_MEASURED },
            { label: 'Error description', value: message.deadLetterErrorDescription ?? NOT_MEASURED },
          ]}
        />

        {/*
          Collapsed by default, and rendered as TEXT: React escapes it, so a body
          that happens to contain markup is shown, never interpreted. Shown
          exactly as received — not re-formatted as JSON, which would round large
          numbers and reorder nothing a reader could then trust.
        */}
        <details>
          <summary style={{ cursor: 'pointer' }}>
            <Text span size="sm">Body ({exactNumber(message.body.length)} characters)</Text>
          </summary>
          {message.body.length > 0 ? (
            <Code
              block
              mt="xs"
              style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflowY: 'auto' }}
            >
              {message.body}
            </Code>
          ) : (
            // Said in prose, not put in the <pre>: a placeholder there would read as the body.
            <Text size="sm" c="dimmed" mt="xs">The body is empty.</Text>
          )}
        </details>
      </Stack>
    </Paper>
  )
}

/** The first line of a server string, capped the way errorSummary caps an error. */
function firstLine(text: string): string | null {
  const first = text.split(/\r?\n/, 1)[0].trim()
  if (!first) return null
  return first.length > 200 ? `${first.slice(0, 200)}…` : first
}
